/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import React, { useEffect, useState } from 'react';

import { css, cssMap, jsx, keyframes } from '@compiled/react';

import { useIsReducedMotion } from '@atlaskit/motion';
import { token, useThemeObserver } from '@atlaskit/tokens';

// These colors were copied from the original implementation.
enum DarkThemeStops {
	BLUE800 = '#0055CC',
	TEAL500 = '#37B4C3',
	YELLOW500 = '#CF9F02',
}

enum LightThemeStops {
	BLUE600 = '#1D7AFC',
	TEAL300 = '#8BDBE5',
	YELLOW200 = '#F8E6A0',
}

enum UpdatedDarkThemeStops {
	ORANGE = '#FCA700',
	GREEN = '#82B536',
	BLUE = '#357DE8',
	PURPLE = '#BF63F3',
}

enum UpdatedLightThemeStops {
	ORANGE = '#FCA700',
	GREEN = '#6A9A23',
	BLUE = '#1868DB',
	PURPLE = '#AF59E0',
}

const PRE_PULSE_ANIMATION_DURATION = 200;
const TOTAL_PULSE_ANIMATION_DURATION = 800;

// Helper function to convert hex color to rgba with alpha
const hexToRgba = (hex: string, alpha: number): string => {
	const r = parseInt(hex.slice(1, 3), 16);
	const g = parseInt(hex.slice(3, 5), 16);
	const b = parseInt(hex.slice(5, 7), 16);
	return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// This injects a CSS property into the document for the --gradient-angle variable.
// This is needed so the browser understands how to animate the gradient-angle as an angle
// value. This is done this way because compiled doesn't support CSS variables at this time.
let propertyInjected = false;
const useGradientAngleProperty = () => {
	useEffect(() => {
		if (propertyInjected) {
			return;
		}
		propertyInjected = true;

		try {
			const style = document.createElement('style');
			style.innerHTML = `
      			@property --gradient-angle {
        			syntax: '<angle>';
        			initial-value: 0deg;
        			inherits: false;
      			}
    		`;
			document.head.appendChild(style);
		} catch (e) {
			// ignore: if this fails, the border will still have the gradient, but it will just not animate
		}
	}, []);
};

// Keyframes for rotating the gradient angle
// ts-ignore because jira build is failing, but this is legit
// @ts-ignore
const rotate = keyframes({
	// @ts-ignore
	from: {
		'--gradient-angle': '0deg',
	},
	// @ts-ignore
	to: {
		'--gradient-angle': '360deg',
	},
});

// Rainbow border styles
// Note: the actual gradient background varies by light/dark theme - this is added down below
const borderCss = css({
	'--gradient-angle': '0deg',
	position: 'relative',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	paddingTop: '1px',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	paddingRight: '1px',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	paddingBottom: '1px',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	paddingLeft: '1px',
	borderRadius: token('radius.small', '4px'),
	// Remove overflow hidden to allow blur glow to be visible
	overflow: 'visible',
});

// Inner content box
const innerBoxCss = css({
	backgroundColor: token('elevation.surface'),
	borderRadius: token('radius.small', '4px'),
	position: 'relative',
	zIndex: 2,
	width: '100%',
});

const lightBackgroundGradientCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
	background: `conic-gradient(from var(--gradient-angle, 270deg), ${LightThemeStops.BLUE600} 10%, ${LightThemeStops.TEAL300} 30%, ${LightThemeStops.YELLOW200} 50%, ${LightThemeStops.TEAL300} 70%, ${LightThemeStops.BLUE600} 90%)`,
});

const darkBackgroundGradientCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
	background: `conic-gradient(from var(--gradient-angle, 270deg),${DarkThemeStops.BLUE800} 10%, ${DarkThemeStops.TEAL500} 30%, ${DarkThemeStops.YELLOW500} 50%, ${DarkThemeStops.TEAL500} 70%, ${DarkThemeStops.BLUE800} 90%)`,
});

const backgroundGlowContainerCss = css({
	position: 'absolute',
	top: 0,
	left: 0,
	right: 0,
	bottom: 0,
	zIndex: 0,
	pointerEvents: 'none',
	background: 'none',
	paddingTop: token('space.200'),
	paddingRight: token('space.200'),
	paddingBottom: token('space.200'),
	paddingLeft: token('space.200'),
});

const backgroundBlurCss = css({
	'--gradient-angle': '0deg',
	width: '100%',
	height: '100%',
	filter: 'blur(12px)',
});

const lightBackgroundDefaultGradientCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
	background: `conic-gradient(from var(--gradient-angle, 38.61deg) at 50% 50%, ${UpdatedLightThemeStops.ORANGE} 0deg, ${UpdatedLightThemeStops.ORANGE} 72.69deg, ${UpdatedLightThemeStops.GREEN} 72.73deg, ${UpdatedLightThemeStops.GREEN} 167.89deg, ${UpdatedLightThemeStops.BLUE} 167.93deg, ${UpdatedLightThemeStops.BLUE} 252.67deg, ${UpdatedLightThemeStops.PURPLE} 252.7deg, ${UpdatedLightThemeStops.PURPLE} 360deg)`,
});

// Light theme blur with alpha transparency for loading states
const lightBackgroundGeneratingGradientCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
	background: `conic-gradient(from var(--gradient-angle, 38.61deg) at 50% 50%, ${hexToRgba(UpdatedLightThemeStops.ORANGE, 1)} 0deg, ${hexToRgba(UpdatedLightThemeStops.ORANGE, 1)} 72.69deg, ${hexToRgba(UpdatedLightThemeStops.GREEN, 1)} 72.73deg, ${hexToRgba(UpdatedLightThemeStops.GREEN, 1)} 167.89deg, ${hexToRgba(UpdatedLightThemeStops.BLUE, 1)} 167.93deg, ${hexToRgba(UpdatedLightThemeStops.BLUE, 1)} 252.67deg, ${hexToRgba(UpdatedLightThemeStops.PURPLE, 1)} 252.7deg, ${hexToRgba(UpdatedLightThemeStops.PURPLE, 1)} 360deg)`,
});

const lightBackgroundTransparentGradientCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
	background: `conic-gradient(from var(--gradient-angle, 38.61deg) at 50% 50%, ${hexToRgba(UpdatedLightThemeStops.ORANGE, 0)} 0deg, ${hexToRgba(UpdatedLightThemeStops.ORANGE, 0)} 72.69deg, ${hexToRgba(UpdatedLightThemeStops.GREEN, 0)} 72.73deg, ${hexToRgba(UpdatedLightThemeStops.GREEN, 0)} 167.89deg, ${hexToRgba(UpdatedLightThemeStops.BLUE, 0)} 167.93deg, ${hexToRgba(UpdatedLightThemeStops.BLUE, 0)} 252.67deg, ${hexToRgba(UpdatedLightThemeStops.PURPLE, 0)} 252.7deg, ${hexToRgba(UpdatedLightThemeStops.PURPLE, 0)} 360deg)`,
});

const lightBackgroundPulseGradientCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
	background: `conic-gradient(from var(--gradient-angle, 38.61deg) at 50% 50%, ${hexToRgba(UpdatedLightThemeStops.ORANGE, 0.5)} 0deg, ${hexToRgba(UpdatedLightThemeStops.ORANGE, 0.5)} 72.69deg, ${hexToRgba(UpdatedLightThemeStops.GREEN, 0.5)} 72.73deg, ${hexToRgba(UpdatedLightThemeStops.GREEN, 0.5)} 167.89deg, ${hexToRgba(UpdatedLightThemeStops.BLUE, 0.5)} 167.93deg, ${hexToRgba(UpdatedLightThemeStops.BLUE, 0.5)} 252.67deg, ${hexToRgba(UpdatedLightThemeStops.PURPLE, 0.5)} 252.7deg, ${hexToRgba(UpdatedLightThemeStops.PURPLE, 0.5)} 360deg)`,
});

const darkBackgroundDefaultGradientCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
	background: `conic-gradient(from var(--gradient-angle, 38.61deg) at 50% 50%, ${UpdatedDarkThemeStops.ORANGE} 0deg, ${UpdatedDarkThemeStops.ORANGE} 72.69deg, ${UpdatedDarkThemeStops.GREEN} 72.73deg, ${UpdatedDarkThemeStops.GREEN} 167.89deg, ${UpdatedDarkThemeStops.BLUE} 167.93deg, ${UpdatedDarkThemeStops.BLUE} 252.67deg, ${UpdatedDarkThemeStops.PURPLE} 252.7deg, ${UpdatedDarkThemeStops.PURPLE} 360deg)`,
});

// Dark theme blur with alpha transparency for loading states
const darkBackgroundGeneratingGradientCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
	background: `conic-gradient(from var(--gradient-angle, 38.61deg) at 50% 50%, ${hexToRgba(UpdatedDarkThemeStops.ORANGE, 1)} 0deg, ${hexToRgba(UpdatedDarkThemeStops.ORANGE, 1)} 72.69deg, ${hexToRgba(UpdatedDarkThemeStops.GREEN, 1)} 72.73deg, ${hexToRgba(UpdatedDarkThemeStops.GREEN, 1)} 167.89deg, ${hexToRgba(UpdatedDarkThemeStops.BLUE, 1)} 167.93deg, ${hexToRgba(UpdatedDarkThemeStops.BLUE, 1)} 252.67deg, ${hexToRgba(UpdatedDarkThemeStops.PURPLE, 1)} 252.7deg, ${hexToRgba(UpdatedDarkThemeStops.PURPLE, 1)} 360deg)`,
});

const darkBackgroundPulseGradientCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
	background: `conic-gradient(from var(--gradient-angle, 38.61deg) at 50% 50%, ${hexToRgba(UpdatedDarkThemeStops.ORANGE, 0.5)} 0deg, ${hexToRgba(UpdatedDarkThemeStops.ORANGE, 0.5)} 72.69deg, ${hexToRgba(UpdatedDarkThemeStops.GREEN, 0.5)} 72.73deg, ${hexToRgba(UpdatedDarkThemeStops.GREEN, 0.5)} 167.89deg, ${hexToRgba(UpdatedDarkThemeStops.BLUE, 0.5)} 167.93deg, ${hexToRgba(UpdatedDarkThemeStops.BLUE, 0.5)} 252.67deg, ${hexToRgba(UpdatedDarkThemeStops.PURPLE, 0.5)} 252.7deg, ${hexToRgba(UpdatedDarkThemeStops.PURPLE, 0.5)} 360deg)`,
});

const darkBackgroundTransparentGradientCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
	background: `conic-gradient(from var(--gradient-angle, 38.61deg) at 50% 50%, ${hexToRgba(UpdatedDarkThemeStops.ORANGE, 0)} 0deg, ${hexToRgba(UpdatedDarkThemeStops.ORANGE, 0)} 72.69deg, ${hexToRgba(UpdatedDarkThemeStops.GREEN, 0)} 72.73deg, ${hexToRgba(UpdatedDarkThemeStops.GREEN, 0)} 167.89deg, ${hexToRgba(UpdatedDarkThemeStops.BLUE, 0)} 167.93deg, ${hexToRgba(UpdatedDarkThemeStops.BLUE, 0)} 252.67deg, ${hexToRgba(UpdatedDarkThemeStops.PURPLE, 0)} 252.7deg, ${hexToRgba(UpdatedDarkThemeStops.PURPLE, 0)} 360deg)`,
});

const loadingStateStyles = cssMap({
	generating: {
		animationName: rotate,
		animationDuration: '2s',
		animationTimingFunction: 'linear',
		animationIterationCount: 'infinite',
	},
	'pre-pulse': {
		paddingBottom: '0px',
		paddingTop: '0px',
		paddingLeft: '0px',
		paddingRight: '0px',
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.small'),
	},
	pulse: {
		animationName: rotate,
		animationDuration: '400ms',
		animationTimingFunction: 'linear',
		animationIterationCount: 'infinite',
		animationDelay: '25ms',
	},
	generated: {
		paddingBottom: '0px',
		paddingTop: '0px',
		paddingLeft: '0px',
		paddingRight: '0px',
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.small'),
	},
});

export type LoadingState = 'generating' | 'pre-pulse' | 'pulse' | 'generated';

export type AnimatedWrapperProps = {
	children: React.ReactNode;
	isAnswerComplete?: boolean;
	loadingState?: LoadingState;
	testId?: string;
};

export const AnimatedWrapper = ({
	children,
	isAnswerComplete = false,
	loadingState,
	testId,
}: AnimatedWrapperProps): JSX.Element => {
	useGradientAngleProperty();
	const { colorMode } = useThemeObserver();
	const isReducedMotion = useIsReducedMotion();

	// Internal state for automatic transitions
	const [internalLoadingState, setInternalLoadingState] = useState<LoadingState>('generating');

	// Use external loadingState if provided, otherwise use internal state
	const currentLoadingState = loadingState ?? internalLoadingState;

	// Handle automatic transitions when answer is complete
	useEffect(() => {
		if (!isAnswerComplete || loadingState) {
			// Don't auto-transition if external loadingState is provided
			return;
		}

		// Start phase 2 transitions
		let timeoutIds: NodeJS.Timeout[] = [];

		// Transition to pre-pulse immediately
		setInternalLoadingState('pre-pulse');

		// Transition to pulse after 200ms
		timeoutIds.push(
			setTimeout(() => {
				setInternalLoadingState('pulse');
			}, PRE_PULSE_ANIMATION_DURATION),
		);

		// Transition to generated after 800ms total (600ms after pulse)
		timeoutIds.push(
			setTimeout(() => {
				setInternalLoadingState('generated');
			}, TOTAL_PULSE_ANIMATION_DURATION),
		);

		// Cleanup timeouts
		return () => {
			timeoutIds.forEach(clearTimeout);
		};
	}, [isAnswerComplete, loadingState]);

	// Reset to generating when answer becomes incomplete
	useEffect(() => {
		if (!isAnswerComplete && !loadingState) {
			setInternalLoadingState('generating');
		}
	}, [isAnswerComplete, loadingState]);

	// Simplified approach for Compiled compatibility
	const isGenerating = currentLoadingState === 'generating';
	const isPrePulse = currentLoadingState === 'pre-pulse';
	const isPulse = currentLoadingState === 'pulse';
	const isGenerated = currentLoadingState === 'generated';

	return (
		<div
			data-testid={testId}
			css={[
				borderCss,
				// Use loading state animation timing when new loading is enabled, otherwise use old animation
				!isReducedMotion
					? colorMode === 'dark'
						? // Apply dark theme gradient with appropriate opacity based on loading state
							isGenerating
							? darkBackgroundGeneratingGradientCss
							: isPrePulse
								? darkBackgroundTransparentGradientCss
								: isPulse
									? darkBackgroundPulseGradientCss
									: isGenerated
										? darkBackgroundTransparentGradientCss
										: darkBackgroundDefaultGradientCss
						: // Apply light theme gradient with appropriate opacity based on loading state
							isGenerating
							? lightBackgroundGeneratingGradientCss
							: isPrePulse
								? lightBackgroundTransparentGradientCss
								: isPulse
									? lightBackgroundPulseGradientCss
									: isGenerated
										? lightBackgroundTransparentGradientCss
										: lightBackgroundDefaultGradientCss
					: colorMode === 'dark'
						? darkBackgroundGradientCss
						: lightBackgroundGradientCss,
				!isReducedMotion && loadingStateStyles[currentLoadingState],
				isReducedMotion && isPrePulse && loadingStateStyles[currentLoadingState],
				isReducedMotion && isGenerated && loadingStateStyles[currentLoadingState],
			]}
		>
			<div css={backgroundGlowContainerCss}>
				<div
					css={[
						backgroundBlurCss,
						// Apply blur gradient with appropriate opacity based on loading state
						colorMode === 'dark'
							? isGenerating
								? darkBackgroundGeneratingGradientCss
								: isPrePulse
									? darkBackgroundTransparentGradientCss
									: isPulse
										? darkBackgroundPulseGradientCss
										: isGenerated
											? darkBackgroundTransparentGradientCss
											: darkBackgroundDefaultGradientCss
							: isGenerating
								? lightBackgroundGeneratingGradientCss
								: isPrePulse
									? lightBackgroundTransparentGradientCss
									: isPulse
										? lightBackgroundPulseGradientCss
										: isGenerated
											? lightBackgroundTransparentGradientCss
											: lightBackgroundDefaultGradientCss,
						// Apply animations only when reduced motion is disabled
						!isReducedMotion ? loadingStateStyles[currentLoadingState] : undefined,
					]}
				/>
			</div>
			<div css={innerBoxCss}>{children}</div>
		</div>
	);
};
